import type { BreadcrumbItem } from '@/components/layout/Breadcrumbs';
import type { ErpModule } from '@/api/platform.api';
import { getModuleBreadcrumbLabel, getModuleEntryByErpModule } from '@/config/moduleEntries';
import {
  SETTINGS_PATH,
  getSettingsCategory,
  getSettingsMasterByPath,
  resolveSettingsModuleKey,
  type SettingsCategory,
  type SettingsModuleKey,
} from '@/config/settingsRegistry';
export function isSettingsPath(pathname: string): boolean {
  const path = pathname.split('?')[0].replace(/\/+$/, '') || '/';
  return path === SETTINGS_PATH || path.startsWith(`${SETTINGS_PATH}/`);
}

function erpModuleFromSettingsKey(moduleKey: SettingsModuleKey): ErpModule | null {
  if (moduleKey === 'PLATFORM') return null;
  return moduleKey as ErpModule;
}

function moduleHomeCrumb(moduleKey: SettingsModuleKey): BreadcrumbItem | null {
  const erpModule = erpModuleFromSettingsKey(moduleKey);
  if (!erpModule) return null;
  const entry = getModuleEntryByErpModule(erpModule);
  if (!entry?.homePath) return null;
  return {
    label: getModuleBreadcrumbLabel(erpModule) ?? entry.label,
    to: entry.homePath,
  };
}

export type BuildSettingsBreadcrumbsOptions = {
  /** Workspace module when viewing `/settings` home (category is platform-wide). */
  activeModule?: ErpModule | null;
  /** Active category hash on `/settings` (e.g. `hr`). */
  hash?: string;
};

/** Skip category crumb when it repeats the workspace module name (e.g. Finance > Settings > Finance). */
function isRedundantSettingsCategory(category: SettingsCategory, moduleKey: SettingsModuleKey): boolean {
  if (moduleKey === 'PLATFORM') return false;
  const entry = getModuleEntryByErpModule(moduleKey as ErpModule);
  if (!entry) return false;
  const categoryLabel = category.label.trim().toLowerCase();
  return [entry.label, entry.breadcrumbLabel, getModuleBreadcrumbLabel(moduleKey as ErpModule)]
    .filter(Boolean)
    .some((label) => label!.trim().toLowerCase() === categoryLabel);
}
/**
 * Settings breadcrumb trail:
 * `{Module home} > Settings > {Category} > {Master}`
 */
export function buildSettingsBreadcrumbs(
  pathname: string,
  options: BuildSettingsBreadcrumbsOptions = {},
): BreadcrumbItem[] {
  const path = pathname.split('?')[0].replace(/\/+$/, '') || SETTINGS_PATH;
  const pathModuleKey = resolveSettingsModuleKey(path);
  const moduleKey: SettingsModuleKey = options.activeModule ?? pathModuleKey;

  const crumbs: BreadcrumbItem[] = [];
  const moduleCrumb = moduleHomeCrumb(moduleKey);
  if (moduleCrumb) crumbs.push(moduleCrumb);

  crumbs.push({ label: 'Settings', to: SETTINGS_PATH });

  if (path === SETTINGS_PATH) {
    const hash = options.hash?.replace(/^#/, '');
    if (hash) {
      const category = getSettingsCategory(hash);
      if (category) {
        crumbs.push({ label: category.label, to: `${SETTINGS_PATH}#${category.key}` });
      }
    }
    return crumbs;
  }

  const parts = path.split('/').filter(Boolean);
  const categoryKey = parts[1];
  const category = getSettingsCategory(categoryKey);
  if (category && !isRedundantSettingsCategory(category, moduleKey)) {
    crumbs.push({
      label: category.label,
      to: `${SETTINGS_PATH}#${category.key}`,
    });
  }
  const master = getSettingsMasterByPath(path);
  if (master && parts.length >= 3) {
    crumbs.push({ label: master.label });
  }

  return crumbs;
}
